/**
 * Where the arrival page's product cards send a customer.
 *
 * They pointed at the shop's own editors — /canvas, /calendar, /produkti —
 * which open a tool and ask for work. Someone who has just finished ordering
 * is not looking for another upload screen; they are deciding whether the
 * next thing is worth wanting. That is what the marketing landing pages are
 * written to do, and each of them ends in its own call to action, so nothing
 * is lost by going the long way round.
 *
 * Each market is its own domain with its own slug (LOCALE_DOMAINS and the
 * *_SLUG maps in the marketing repo), so domain and slug are kept together
 * here as one verified pair — deriving the domain from the storefront host
 * while taking the slug from the locale would produce
 * fotodelavnica.si/duze-zdjecia, which is a 404.
 *
 * Every URL below was checked live before being written down. Locales with no
 * marketing site (cs, it, en, de-AT) get no landing page and fall back to the
 * shop's own route rather than a guess.
 */

export interface NextStepLinks {
  canvas: string;
  calendar: string;
  invitation: string;
}

/**
 * The "from" price each card quotes, per market, in that market's currency.
 *
 * A card with a title and an arrow is a link; a card with a price is an
 * offer. Every figure here was read off the live catalogue or the landing
 * page it links to, not estimated:
 *
 *   canvas      PAPER-40x60, the entry enlargement the /canvas editor opens on
 *   calendar    the A4 price /foto-koledar advertises
 *   invitation  the invitation definition's own price, per card
 *
 * A market with no confirmed figure gets none and the card simply shows no
 * price, which is what every card did before this. Guessing one on the page
 * that just took somebody's money is the worst place to be wrong.
 */
export const NEXT_STEP_PRICES: Record<
  string,
  Partial<Record<keyof NextStepLinks, string>>
> = {
  sl: { canvas: "19,90 €", calendar: "24,90 €", invitation: "1,50 €" },
  hr: { canvas: "19,90 €", calendar: "24,90 €", invitation: "1,50 €" },
  sk: { canvas: "19,90 €", calendar: "24,90 €", invitation: "1,50 €" },
  pl: { canvas: "85 zł", calendar: "109,50 zł", invitation: "6,50 zł" },
};

/** The from-prices for a market, or an empty set when none are confirmed. */
export function nextStepPrices(
  locale: string,
): Partial<Record<keyof NextStepLinks, string>> {
  return NEXT_STEP_PRICES[(locale || "").toLowerCase().split("-")[0]] ?? {};
}

const MARKETS: Record<string, { domain: string; links: NextStepLinks }> = {
  sl: {
    domain: "fotodelavnica.si",
    links: {
      canvas: "velike-fotografije",
      calendar: "spletno-narocilo-fotokoledarja",
      invitation: "vabila",
    },
  },
  hr: {
    domain: "fotoradnja.hr",
    links: {
      canvas: "velike-fotografije",
      calendar: "fotokalendari",
      invitation: "pozivnice",
    },
  },
  sk: {
    domain: "fotospomienky.sk",
    links: {
      canvas: "velke-fotografie",
      calendar: "fotokalendare",
      invitation: "pozvanky",
    },
  },
  pl: {
    domain: "foto-chwila.pl",
    links: {
      canvas: "duze-zdjecia",
      calendar: "fotokalendarz",
      invitation: "zaproszenia",
    },
  },
};

/** The shop's own routes — the fallback, and stable public entries. */
const SHOP_ROUTES: NextStepLinks = {
  canvas: "canvas",
  calendar: "calendar",
  invitation: "produkti",
};

/**
 * Landing-page URLs for a market, or the shop's own routes when that market
 * has no marketing site.
 */
export function nextStepLinks(locale: string): NextStepLinks {
  const lang = (locale || "").toLowerCase().split("-")[0];
  const market = MARKETS[lang];
  if (!market) {
    return {
      canvas: `/${locale}/${SHOP_ROUTES.canvas}`,
      calendar: `/${locale}/${SHOP_ROUTES.calendar}`,
      invitation: `/${locale}/${SHOP_ROUTES.invitation}`,
    };
  }
  const url = (slug: string) => `https://${market.domain}/${slug}`;
  return {
    canvas: url(market.links.canvas),
    calendar: url(market.links.calendar),
    invitation: url(market.links.invitation),
  };
}

/** True when the link leaves the shop, so the anchor can be a plain one. */
export const isExternal = (href: string) => href.startsWith("http");
